import { useEffect, useMemo, useState } from 'react';
import {
  GRUPPO_CONTESTO_GIOCO,
  GRUPPO_MALLO,
  suggerisciMallo,
  SuggerimentoMallo,
} from '@leopart/calc';
import {
  ITrainingBlock,
  ITrainingCategory,
  TB_RESPONSIBLE,
  TbResponsible,
} from '../../../models/allenamenti';
import { subcategorieDelleMacro, sottoAmmesse } from '../taxonomy.utils';

export interface IBlockFormValues {
  localDate: string;
  categoryIds: string[];
  subcategoryIds: string[];
  title: string;
  durationMinutes: number;
  responsible: TbResponsible;
  /** Contesto di gioco e Mallo (ADR 029): `null` = campo vuoto. */
  fieldWidthM: number | null;
  fieldLengthM: number | null;
  playersOnField: number | null;
  effectiveMinutes: number | null;
  malloWeight: number | null;
  flowType: 'continuo' | 'intermittente' | 'analitico' | null;
}

const DEFAULT_DURATION = 15;

const emptyValues = (localDate: string): IBlockFormValues => ({
  localDate,
  categoryIds: [],
  subcategoryIds: [],
  title: '',
  durationMinutes: DEFAULT_DURATION,
  responsible: TB_RESPONSIBLE.PROF,
  fieldWidthM: null,
  fieldLengthM: null,
  playersOnField: null,
  effectiveMinutes: null,
  malloWeight: null,
  flowType: null,
});

/**
 * Stato del form del blocco.
 *
 * Le sottocategorie dipendono dalle categorie scelte: togliendo una categoria
 * vanno tolte anche le sue sotto, altrimenti resterebbero agganciate a un
 * parent non più selezionato e il server rifiuterebbe il salvataggio.
 *
 * Si SCARTANO le orfane invece di azzerare tutto: chi deseleziona la terza di
 * tre categorie non si aspetta di perdere anche le sotto delle prime due.
 *
 * I gruppi «Contesto di gioco» e «Mallo» compaiono se ALMENO UNA categoria
 * scelta li abilita (`fieldsEnabled`, ADR 029). I valori NON si azzerano
 * quando il gruppo sparisce: restano nel documento, invisibili — chi
 * ri-aggiunge la categoria li ritrova.
 */
export const useBlockForm = (
  categories: ITrainingCategory[],
  defaultDate: string,
  editing: ITrainingBlock | null,
  isOpen: boolean,
) => {
  const [values, setValues] = useState<IBlockFormValues>(() =>
    emptyValues(defaultDate),
  );

  useEffect(() => {
    if (!isOpen) return;
    setValues(
      editing
        ? {
            localDate: editing.localDate,
            categoryIds: editing.categoryIds ?? [],
            subcategoryIds: editing.subcategoryIds ?? [],
            title: editing.title,
            durationMinutes: editing.durationMinutes,
            responsible: editing.responsible,
            fieldWidthM: editing.fieldWidthM ?? null,
            fieldLengthM: editing.fieldLengthM ?? null,
            playersOnField: editing.playersOnField ?? null,
            effectiveMinutes: editing.effectiveMinutes ?? null,
            malloWeight: editing.malloWeight ?? null,
            flowType: editing.flowType ?? null,
          }
        : emptyValues(defaultDate),
    );
  }, [isOpen, editing, defaultDate]);

  const setField = <K extends keyof IBlockFormValues>(
    key: K,
    value: IBlockFormValues[K],
  ) => {
    setValues((prev) => {
      if (key !== 'categoryIds') return { ...prev, [key]: value };

      const categoryIds = value as string[];
      const ammesse = sottoAmmesse(categories, categoryIds);
      return {
        ...prev,
        categoryIds,
        subcategoryIds: prev.subcategoryIds.filter((s) => ammesse.has(s)),
      };
    });
  };

  /**
   * Le sotto selezionabili: l'unione di quelle delle categorie scelte, con
   * l'etichetta della macro davanti — «Forza · Forza Massima». Senza il
   * prefisso, con due categorie scelte l'elenco diventerebbe un blocco di nomi
   * di cui non si capisce a chi appartengono.
   */
  const subcategories = useMemo(
    () => subcategorieDelleMacro(categories, values.categoryIds),
    [categories, values.categoryIds],
  );

  /** Unione dei gruppi abilitati dalle categorie selezionate (ADR 029). */
  const gruppiAttivi = useMemo(
    () =>
      new Set(
        categories
          .filter((c) => values.categoryIds.includes(c.slug))
          .flatMap((c) => c.fieldsEnabled ?? []),
      ),
    [categories, values.categoryIds],
  );
  const showGameContext = gruppiAttivi.has(GRUPPO_CONTESTO_GIOCO);
  const showMallo = gruppiAttivi.has(GRUPPO_MALLO);

  /** Gli slug abilitati al gruppo mallo: input del suggeritore. */
  const categorieMallo = useMemo(
    () =>
      categories
        .filter((c) => (c.fieldsEnabled ?? []).includes(GRUPPO_MALLO))
        .map((c) => c.slug),
    [categories],
  );

  /**
   * Suggerimento peso+flusso, ricalcolato live su titolo e categorie. Il
   * banner si mostra solo quando aggiunge qualcosa: campi vuoti, o valori
   * diversi dal suggerito. Applicarlo non impedisce di correggere a mano.
   */
  const rawSuggestion: SuggerimentoMallo | null = useMemo(
    () =>
      showMallo
        ? suggerisciMallo(values.categoryIds, values.title, categorieMallo)
        : null,
    [showMallo, values.categoryIds, values.title, categorieMallo],
  );
  const suggestion =
    rawSuggestion &&
    (rawSuggestion.malloWeight !== values.malloWeight ||
      rawSuggestion.flowType !== values.flowType)
      ? rawSuggestion
      : null;

  const applySuggestion = () => {
    if (!rawSuggestion) return;
    setValues((prev) => ({
      ...prev,
      malloWeight: rawSuggestion.malloWeight,
      flowType: rawSuggestion.flowType,
    }));
  };

  const isValid =
    !!values.localDate &&
    values.categoryIds.length > 0 &&
    values.title.trim().length > 0 &&
    values.durationMinutes >= 1 &&
    values.durationMinutes <= 600 &&
    // Vincolo del backend (EFFECTIVE_OVER_DURATION): meglio bloccarlo qui.
    (values.effectiveMinutes == null ||
      values.effectiveMinutes <= values.durationMinutes);

  const toPayload = () => ({
    localDate: values.localDate,
    categoryIds: values.categoryIds,
    subcategoryIds: values.subcategoryIds,
    title: values.title.trim(),
    durationMinutes: values.durationMinutes,
    responsible: values.responsible,
    // Sempre inclusi, anche quando i gruppi sono nascosti: i valori esistenti
    // si conservano, non si azzerano (decisione ticket #5).
    fieldWidthM: values.fieldWidthM,
    fieldLengthM: values.fieldLengthM,
    playersOnField: values.playersOnField,
    effectiveMinutes: values.effectiveMinutes,
    malloWeight: values.malloWeight,
    flowType: values.flowType,
  });

  return {
    values,
    setField,
    subcategories,
    showGameContext,
    showMallo,
    suggestion,
    applySuggestion,
    isValid,
    toPayload,
  };
};
